import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { CalendarClock, CheckCircle2, ClipboardCheck, Clock, ExternalLink, MapPin, Phone, Trash2, X, Zap } from 'lucide-react';
import { Negociacao } from '../../../../types';
import { Modalidade, isRemota } from '../../../../src/lib/modalidade';

export type GerenciarTarefaAcao = 'concluir' | 'reagendar' | 'cancelar';

export interface GerenciarTarefaResultado {
    acao: GerenciarTarefaAcao;
    observacao: string;
    /** Reagendar: nova data (YYYY-MM-DD). */
    novaData?: string;
    /** Concluir: data do retorno agendado (YYYY-MM-DD) ou null. */
    proximaVisitaData?: string | null;
    proximaVisitaModalidade?: Modalidade;
}

interface GerenciarTarefaModalProps {
    /** Objeto da tarefa vindo do feed do lead (id, titulo, descricao, prioridade, vencimento...). */
    tarefa: any;
    negotiation: Negociacao;
    onClose: () => void;
    onConfirm: (resultado: GerenciarTarefaResultado) => Promise<void>;
    /** Abre a tarefa completa (página Tarefas) em nova aba — não troca a rota atual. */
    onAbrirCompleta: () => void;
}

const addDiasIso = (n: number) => { const d = new Date(); d.setDate(d.getDate() + n); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const fmtData = (iso: string) => new Date(iso + 'T12:00:00').toLocaleDateString('pt-BR');

const prioridadeBadge = (p?: string) => {
    switch (p) {
        case 'alta': return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
        case 'media': return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
        default: return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
    }
};
const prioridadeLabel = (p?: string) => p === 'alta' ? '🔴 Alta' : p === 'media' ? '🟡 Média' : '🟢 Baixa';

const ACOES: Array<{ value: GerenciarTarefaAcao; label: string }> = [
    { value: 'concluir', label: 'Concluir' },
    { value: 'reagendar', label: 'Reagendar' },
    { value: 'cancelar', label: 'Cancelar' },
];

/**
 * "Gerenciar Tarefa" inline sobre o Lead (padrão visual do "Concluir Visita").
 * Resolve a tarefa (concluir/reagendar/cancelar) sem sair do Kanban.
 */
const GerenciarTarefaModal: React.FC<GerenciarTarefaModalProps> = ({ tarefa, negotiation, onClose, onConfirm, onAbrirCompleta }) => {
    const [acao, setAcao] = useState<GerenciarTarefaAcao>('concluir');
    const [observacao, setObservacao] = useState('');
    const [novaData, setNovaData] = useState(addDiasIso(1));
    const [agendarRetorno, setAgendarRetorno] = useState(false);
    const [retornoData, setRetornoData] = useState(addDiasIso(1));
    const [retornoModalidade, setRetornoModalidade] = useState<Modalidade>('presencial');
    const [salvando, setSalvando] = useState(false);

    const podeConfirmar = acao === 'reagendar' ? !!novaData : true;

    const confirmar = async () => {
        if (!podeConfirmar || salvando) return;
        setSalvando(true);
        try {
            await onConfirm({
                acao,
                observacao: observacao.trim(),
                novaData: acao === 'reagendar' ? novaData : undefined,
                proximaVisitaData: acao === 'concluir' && agendarRetorno ? retornoData : null,
                proximaVisitaModalidade: acao === 'concluir' && agendarRetorno ? retornoModalidade : undefined,
            });
        } finally {
            setSalvando(false);
        }
    };

    const confirmLabel = salvando
        ? 'Salvando...'
        : acao === 'concluir' ? (agendarRetorno ? 'Concluir e agendar retorno' : 'Concluir Tarefa')
            : acao === 'reagendar' ? `Reagendar p/ ${fmtData(novaData)}`
                : 'Cancelar Tarefa';

    const confirmColor = acao === 'cancelar'
        ? 'bg-red-500 hover:bg-red-600'
        : acao === 'reagendar' ? 'bg-primary hover:opacity-90' : 'bg-emerald-500 hover:bg-emerald-600';

    const telefone = negotiation.whatsapp;

    return createPortal(
        <div className="fixed inset-0 z-[2000] flex items-end sm:items-center justify-center sm:p-4 bg-black/50" onClick={onClose}>
            <div
                className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md flex flex-col overflow-hidden max-h-[92vh]"
                onClick={e => e.stopPropagation()}
            >
                {/* Cabeçalho */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-2.5 min-w-0">
                        <div className="p-2 rounded-xl shrink-0 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                            <ClipboardCheck size={18} />
                        </div>
                        <div className="min-w-0">
                            <h2 className="text-sm font-bold text-gray-900 dark:text-white">Gerenciar Tarefa</h2>
                            <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{negotiation.titulo}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 shrink-0">
                        <X size={18} />
                    </button>
                </div>

                <div className="p-5 space-y-4 overflow-y-auto">
                    {/* Contexto do lead */}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500 dark:text-gray-400">
                        {(negotiation.bairro || negotiation.cidade) && (
                            <span className="flex items-center gap-1"><MapPin size={11} /> {[negotiation.bairro, negotiation.cidade].filter(Boolean).join(', ')}</span>
                        )}
                        {negotiation.segmento && <span>{negotiation.segmento}</span>}
                        {(negotiation.consumo_medio_kwh || 0) > 0 && <span className="flex items-center gap-0.5 font-semibold text-amber-600 dark:text-amber-400"><Zap size={11} /> {Math.round(negotiation.consumo_medio_kwh!).toLocaleString('pt-BR')} kWh</span>}
                        {telefone && <span>{telefone}</span>}
                    </div>

                    {/* Info da tarefa */}
                    <div className="rounded-xl bg-gray-50 dark:bg-gray-700/40 border border-gray-100 dark:border-gray-700 p-3 space-y-1.5">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${prioridadeBadge(tarefa.prioridade)}`}>{prioridadeLabel(tarefa.prioridade)}</span>
                            {tarefa.vencimento && (
                                <span className="flex items-center gap-1 text-[11px] text-gray-500 dark:text-gray-400"><Clock size={11} /> Vence {fmtData(String(tarefa.vencimento).slice(0, 10))}</span>
                            )}
                        </div>
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{tarefa.titulo}</p>
                        {tarefa.descricao && <p className="text-xs text-gray-500 dark:text-gray-400">{tarefa.descricao}</p>}
                    </div>

                    {/* Ação da tarefa */}
                    <div>
                        <label className="text-xs font-bold text-gray-700 dark:text-gray-200 block mb-1.5">Ação da tarefa</label>
                        <div className="grid grid-cols-3 gap-1.5">
                            {ACOES.map(o => (
                                <button
                                    key={o.value}
                                    onClick={() => setAcao(o.value)}
                                    className={`px-2.5 py-2 rounded-xl text-xs font-semibold border transition-all ${acao === o.value
                                        ? (o.value === 'cancelar' ? 'bg-red-50 border-red-300 text-red-600 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400' : 'bg-primary/10 border-primary/40 text-primary')
                                        : 'bg-gray-50 dark:bg-gray-700/40 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-gray-300'}`}
                                >
                                    {o.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Reagendar: data */}
                    {acao === 'reagendar' && (
                        <div className="rounded-xl border border-primary/30 bg-primary/5 p-3 space-y-2.5">
                            <p className="text-xs font-bold text-primary flex items-center gap-1.5"><CalendarClock size={13} /> Nova data</p>
                            <div className="grid grid-cols-3 gap-1.5">
                                {[{ label: 'Amanhã', value: addDiasIso(1) }, { label: 'Depois de amanhã', value: addDiasIso(2) }, { label: 'Próxima semana', value: addDiasIso(7) }].map(a => (
                                    <button
                                        key={a.label}
                                        onClick={() => setNovaData(a.value)}
                                        className={`px-2 py-2 rounded-xl text-[11px] font-semibold border transition-all ${novaData === a.value ? 'bg-primary/10 border-primary/40 text-primary' : 'bg-white dark:bg-gray-700/40 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-gray-300'}`}
                                    >
                                        {a.label}
                                    </button>
                                ))}
                            </div>
                            <input
                                type="date"
                                value={novaData}
                                min={addDiasIso(0)}
                                onChange={e => setNovaData(e.target.value)}
                                className="w-full px-3 py-2.5 text-sm rounded-xl bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                            />
                        </div>
                    )}

                    {/* Observação */}
                    <div>
                        <label className="text-xs font-bold text-gray-700 dark:text-gray-200 block mb-1.5">
                            {acao === 'cancelar' ? 'Motivo do cancelamento' : 'Observação'} <span className="text-gray-400 font-normal">(opcional)</span>
                        </label>
                        <textarea
                            value={observacao}
                            onChange={e => setObservacao(e.target.value)}
                            rows={3}
                            placeholder={acao === 'cancelar' ? 'Ex: cliente pediu para não retornar...' : 'Registro do que foi feito...'}
                            className="w-full px-3 py-2.5 text-sm rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
                        />
                    </div>

                    {/* Concluir: próxima ação (agendar retorno) */}
                    {acao === 'concluir' && (
                        <div>
                            <label className="text-xs font-bold text-gray-700 dark:text-gray-200 block mb-1.5">
                                Próxima ação <span className="text-gray-400 font-normal">(opcional)</span>
                            </label>
                            <select
                                value={agendarRetorno ? 'retorno' : ''}
                                onChange={e => setAgendarRetorno(e.target.value === 'retorno')}
                                className="w-full text-sm px-3 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100 focus:outline-none"
                            >
                                <option value="">Sem ação necessária</option>
                                <option value="retorno">Agendar retorno</option>
                            </select>
                            {agendarRetorno && (
                                <div className="mt-2.5 rounded-xl border border-primary/30 bg-primary/5 p-3 space-y-2.5">
                                    <div className="grid grid-cols-2 gap-2">
                                        <button type="button" onClick={() => setRetornoModalidade('presencial')} className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold border transition-all ${retornoModalidade === 'presencial' ? 'bg-primary/10 border-primary/40 text-primary' : 'bg-white dark:bg-gray-700/40 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300'}`}>
                                            <MapPin size={13} /> Presencial
                                        </button>
                                        <button type="button" onClick={() => setRetornoModalidade('remota')} className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold border transition-all ${isRemota(retornoModalidade) ? 'bg-primary/10 border-primary/40 text-primary' : 'bg-white dark:bg-gray-700/40 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300'}`}>
                                            <Phone size={13} /> Remota
                                        </button>
                                    </div>
                                    <input
                                        type="date"
                                        value={retornoData}
                                        min={addDiasIso(0)}
                                        onChange={e => setRetornoData(e.target.value)}
                                        className="w-full px-3 py-2 text-sm rounded-xl bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                                    />
                                    <p className="text-[10px] text-gray-500 dark:text-gray-400">Ao concluir, o retorno é criado na agenda de {fmtData(retornoData)}.</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Ações rápidas */}
                    <button
                        type="button"
                        onClick={onAbrirCompleta}
                        className="w-full flex items-center justify-center gap-1.5 px-2.5 py-2 rounded-xl text-xs font-bold border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/40 text-gray-600 dark:text-gray-300 hover:border-primary/40 hover:text-primary transition-all"
                    >
                        <ExternalLink size={13} /> Abrir tarefa completa
                    </button>

                    {/* Confirmar */}
                    <button
                        onClick={confirmar}
                        disabled={!podeConfirmar || salvando}
                        className={`w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-bold rounded-xl text-white transition-opacity disabled:opacity-40 ${confirmColor}`}
                    >
                        {acao === 'cancelar' ? <Trash2 size={16} /> : acao === 'reagendar' ? <CalendarClock size={16} /> : <CheckCircle2 size={16} />}
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default GerenciarTarefaModal;
